import { apiClient, webClient } from './client'
import { Capacitor } from '@capacitor/core'
import type { DiagnosticoAgendamento } from '@/types/diagnostico'

export interface Banner {
  id: number
  title: string
  text?: string
  banner_type?: 'image' | 'color'
  image_path?: string
  image_url?: string
  background_color?: string
  text_color?: string
  icon?: string
  link_url?: string
  display_duration?: number
  priority?: number
}

export interface Categoria {
  id: string
  nome: string
  icone: string
  ativa: boolean
}

export interface Agendamento {
  id: number
  proposta_atual?: { id: number; versao: number; status: string } | null
  user_id?: number
  cliente_id?: number
  oficina_id?: number
  veiculo_id?: number
  titulo?: string
  data_hora_inicio?: string
  data_hora_fim?: string
  data_hora_proposta?: string
  data_hora_fim_proposta?: string
  data?: string
  tipo?: string
  responsavel?: string
  status: string
  valor_estimado?: number
  observacoes?: string
  observacoes_oficina?: string
  codigo_agendamento?: string
  ativo?: boolean
  servico?: string
  duracao_minutos?: number
  oficina?: {
    id?: number
    nome?: string
    name?: string
    endereco?: string
    telefone?: string
    cidade?: string
  }
  veiculo?: {
    id?: number
    marca?: string
    modelo?: string
    placa?: string
    placa_formatada?: string
    ano?: number
  }
  diagnostico?: DiagnosticoAgendamento | null
  ordemServico?: {
    id: number
    formas_pagamento_propostas?: string[]
  }
  ordem_servico?: {
    id: number
    formas_pagamento_propostas?: string[]
  }
}

export interface Slot {
  hora: string
  hora_fim: string
  vagas: number
  duracao_minutos: number
  mecanicos?: Array<{ id: number; nome: string }>
}

export interface Oficina {
  id: number
  nome?: string
  name?: string
  latitude?: number
  longitude?: number
  distancia?: number
  distancia_km?: number
  distancia_formatada?: string
  avaliacao_media?: number
  rating?: number
  total_avaliacoes?: number
  reviews_count?: number
  servicos_oferecidos?: string[]
  servicos_lista?: string[]
  endereco?: string
  cidade?: string
  telefone?: string
  horario_status?: string | { text: string }
}

export interface HomeData {
  user: {
    id: number
    name: string
    foto_perfil_url?: string
  }
  banners: Banner[]
  categoriasAtivas: Categoria[]
  historicoAgendamentos: Agendamento[]
  oficinasProximas: Oficina[]
}

export interface BuscarParams {
  endereco?: string
  servico?: string
  ordenarPor?: 'proximidade' | 'avaliacao' | 'nome'
  avaliacaoMinima?: number | string
  raio?: number
  latitude?: number
  longitude?: number
}

export interface BuscarResponse {
  oficinas: Oficina[]
  total: number
}

export interface IpvaParcela {
  id: number
  numero_parcela: number
  valor: number
  data_vencimento: string
  data_pagamento?: string
  status: 'pendente' | 'pago' | 'vencido'
  metodo_pagamento?: string
  transacao_id?: string
}

export interface IpvaParcelamento {
  id: number
  veiculo: {
    id: number
    marca: string
    modelo: string
    placa_formatada: string
    ano: number
  }
  ano_exercicio: number
  valor_ipva: number
  taxa_administrativa: number
  valor_total: number
  parcelas: number
  total_parcelas: number
  valor_parcela: number
  status: 'pendente' | 'pago' | 'vencido' | 'ativo' | 'quitado' | 'cancelado'
  data_vencimento_primeira_parcela: string
  parcelas_lista?: IpvaParcela[]
  created_at: string
  updated_at: string
}

export interface ConsultarValorIpvaResponse {
  success: boolean
  valor_ipva: number
  dados_veiculo: {
    marca: string
    modelo: string
    placa: string
    ano: number
  }
  detalhes?: {
    valor_ipva: number
    valor_multas: number
    valor_licenciamento: number
    valor_dpvat: number
    data_vencimento?: string
    status: string
  }
  message?: string
}

export interface ContratarParcelamentoRequest {
  veiculo_id: number
  valor_ipva: number
  taxa_administrativa: number
  valor_total: number
  parcelas: number
  valor_parcela: number
}

export interface ContratarParcelamentoResponse {
  success: boolean
  message: string
  parcelamento_id?: number
  redirect_url?: string
}

export interface IpvaConsulta {
  id: number
  veiculo?: {
    id: number
    marca: string
    modelo: string
    placa_formatada: string
    ano: number
  }
  placa: string
  ano_exercicio: number
  valor_ipva?: number
  valor_total?: number
  status_consulta: 'pendente' | 'consultado' | 'erro'
  status_pagamento: 'pendente' | 'pago' | 'vencido' | 'cancelado'
  data_vencimento?: string
  consultado_em?: string
  created_at: string
  updated_at: string
}

export interface ContratarConsultaRequest {
  placa: string
  ano: number
  emailReminders?: boolean
  smsReminders?: boolean
  monthlyReports?: boolean
  pdfExport?: boolean
}

export interface ContratarConsultaResponse {
  success: boolean
  message: string
  consulta?: IpvaConsulta
  redirect_url?: string
}

export interface ToggleFavoritaResponse {
  success?: boolean
  message?: string
  is_favorita?: boolean
  data?: {
    is_favorita?: boolean
  }
}

export interface VehicleLookupData {
  placa: string
  marca?: string
  modelo?: string
  ano_fabricacao?: number
  ano_modelo?: number
  ano?: number
  cor?: string
  combustivel?: string
  is_mock?: boolean
}

export interface VehicleLookupResponse {
  success: boolean
  data?: VehicleLookupData
  message?: string
}

export interface CriarAgendamentoOficinaRequest {
  veiculo_id: number
  servicos: string
  data_agendamento: string
  horario: string
  observacoes?: string | null
  debug_mode?: boolean
}

export interface CriarAgendamentoOficinaResponse {
  success: boolean
  message?: string
  data?: any
}

export interface ServicoFixo {
  id: number
  oficina_id: number
  nome: string
  descricao?: string | null
  categoria?: string | null
  preco_base: number
  tempo_estimado_minutos?: number | null
  tipo_preco: 'fixo' | 'variavel' | 'orcamento' | string
  requer_agendamento: boolean
  disponivel_online: boolean
}

export interface ServicosFixosResponse {
  success: boolean
  data: {
    oficina: {
      id: number
      nome: string
    }
    servicos: ServicoFixo[]
  }
}

export interface AgendarServicoFixoRequest {
  oficina_id: number
  servico_id: number
  veiculo_id: number
  data_hora: string
  observacoes?: string | null
}

export interface AgendarServicoFixoResponse {
  success: boolean
  message?: string
  data?: {
    id: number
    codigo: string
    status: string
    data_hora: string
    duracao_minutos: number
    valor_estimado: number
    oficina: {
      id: number
      nome: string
    }
    servico: {
      id: number
      nome: string
      categoria?: string | null
      tempo_estimado_minutos?: number | null
      preco_base: number
    }
  }
}

function shouldFallbackToLegacyWebRoute(error: any): boolean {
  const status = error?.response?.status
  // 404: route not found
  // 405: method not allowed
  // 419: CSRF token mismatch (common on native apps where cookies don't work)
  return status === 404 || status === 405 || status === 419
}

const usuarioApi = {
  async getHomeData(): Promise<HomeData> {
    return apiClient.get<HomeData>('/usuario/home')
  },

  async buscarOficinas(params: BuscarParams): Promise<BuscarResponse> {
    const queryParams = new URLSearchParams()
    if (params.endereco) queryParams.append('endereco', params.endereco)
    if (params.servico) queryParams.append('servico', params.servico)
    if (params.ordenarPor) queryParams.append('ordenarPor', params.ordenarPor)
    if (params.avaliacaoMinima) queryParams.append('avaliacaoMinima', String(params.avaliacaoMinima))
    if (params.raio) queryParams.append('raio', String(params.raio))
    if (params.latitude) queryParams.append('latitude', String(params.latitude))
    if (params.longitude) queryParams.append('longitude', String(params.longitude))

    return apiClient.get<BuscarResponse>(`/usuario/buscar?${queryParams.toString()}`)
  },

  async getOficinasProximas(): Promise<Oficina[]> {
    return apiClient.get<Oficina[]>('/usuario/oficinas/proximas')
  },

  async getFavoritos(): Promise<Oficina[]> {
    const response = await apiClient.get<{ success: boolean; data: Oficina[]; total: number }>('/usuario/favoritos')
    return response.data || []
  },

  async getPerfil() {
    return apiClient.get('/usuario/perfil')
  },

  async atualizarPerfil(formData: FormData): Promise<{ success: boolean; message?: string; errors?: any }> {
    const config = {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    }

    // For native Capacitor apps, use API client first (no CSRF required)
    // API routes use Sanctum Bearer token auth which works reliably on mobile
    const isNative = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.()

    if (isNative) {
      try {
        return await apiClient.upload<{ success: boolean; message?: string; errors?: any }>('/usuario/perfil', formData, config)
      } catch (error: any) {
        if (!shouldFallbackToLegacyWebRoute(error)) {
          throw error
        }
        // Fallback to web route (unlikely to work on native, but defensive)
        return await webClient.upload<{ success: boolean; message?: string; errors?: any }>('/usuario/perfil', formData, config)
      }
    }

    // Web-first for browser environments
    try {
      return await webClient.upload<{ success: boolean; message?: string; errors?: any }>('/usuario/perfil', formData, config)
    } catch (error: any) {
      if (!shouldFallbackToLegacyWebRoute(error)) {
        throw error
      }
      // Fallback para ambientes que expõem atualização de perfil no namespace /api.
      return await apiClient.upload<{ success: boolean; message?: string; errors?: any }>('/usuario/perfil', formData, config)
    }
  },

  async getOficina(id: number) {
    return apiClient.get(`/usuario/oficinas/${id}`)
  },

  async getServicosFixos(oficinaId: number): Promise<ServicosFixosResponse> {
    return apiClient.get<ServicosFixosResponse>(`/usuario/oficinas/${oficinaId}/servicos-fixos`)
  },

  async toggleOficinaFavorita(id: number): Promise<ToggleFavoritaResponse> {
    try {
      // Canonical mobile endpoint em /api.
      return await apiClient.post<ToggleFavoritaResponse>(`/usuario/oficinas/${id}/favorita`)
    } catch (error: any) {
      if (!shouldFallbackToLegacyWebRoute(error)) {
        throw error
      }
      // Fallback para instalações legadas onde a rota existe apenas no namespace web.
      return webClient.post<ToggleFavoritaResponse>(`/usuario/oficina/${id}/favorita`)
    }
  },

  async criarAgendamentoOficina(id: number, payload: CriarAgendamentoOficinaRequest): Promise<CriarAgendamentoOficinaResponse> {
    try {
      return await apiClient.post<CriarAgendamentoOficinaResponse>(`/usuario/agendar/${id}`, payload)
    } catch (error: any) {
      if (!shouldFallbackToLegacyWebRoute(error)) {
        throw error
      }
      return webClient.post<CriarAgendamentoOficinaResponse>(`/usuario/agendar/${id}`, payload)
    }
  },

  async agendarServicoFixo(payload: AgendarServicoFixoRequest): Promise<AgendarServicoFixoResponse> {
    return apiClient.post<AgendarServicoFixoResponse>('/usuario/agendamentos/servico-fixo', payload)
  },

  async atualizarNotificacoesPerfil(formData: FormData): Promise<{ success: boolean; message?: string }> {
    const config = {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    }

    try {
      // Esta rota existe no backend web (routes/web.php), não em /api.
      // Usamos webClient primeiro para evitar 404 em /api/usuario/perfil/notifications.
      return await webClient.upload<{ success: boolean; message?: string }>('/usuario/perfil/notifications', formData, config)
    } catch (error: any) {
      if (!shouldFallbackToLegacyWebRoute(error)) {
        throw error
      }
      // Fallback defensivo para ambientes que já tenham endpoint equivalente em /api.
      return await apiClient.upload<{ success: boolean; message?: string }>('/usuario/perfil/notifications', formData, config)
    }
  },

  async updateLocation(latitude: number, longitude: number) {
    return apiClient.post('/usuario/update-location', {
      latitude,
      longitude
    })
  },

  async getIpvaParcelamentos(params?: { status?: string; ano?: string }): Promise<{ parcelamentos: IpvaParcelamento[], consultas?: IpvaConsulta[], estatisticas: any, veiculos?: any[] }> {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.ano) queryParams.append('ano', params.ano)

    const url = `/usuario/ipva${queryParams.toString() ? '?' + queryParams.toString() : ''}`
    // Adicionar header para indicar que queremos JSON (apiClient já tem Accept: application/json por padrão)
    const response = await apiClient.get(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    // Se a resposta for um objeto com parcelamentos, retornar como está
    // Se for array direto, transformar em objeto
    if (Array.isArray(response)) {
      return {
        parcelamentos: response,
        estatisticas: {
          total_a_pagar: 0,
          pagos: 0,
          pendentes: 0,
          vencidos: 0
        }
      }
    }
    return response as any
  },

  async consultarValorIpva(veiculoId: number): Promise<ConsultarValorIpvaResponse> {
    return apiClient.post<ConsultarValorIpvaResponse>('/usuario/ipva/consultar-valor', {
      veiculo_id: veiculoId
    })
  },

  async contratarParcelamento(dados: ContratarParcelamentoRequest): Promise<ContratarParcelamentoResponse> {
    return apiClient.post<ContratarParcelamentoResponse>('/usuario/ipva/contratar', dados)
  },

  async contratarConsulta(dados: ContratarConsultaRequest): Promise<ContratarConsultaResponse> {
    return apiClient.post<ContratarConsultaResponse>('/usuario/ipva/contratar-consulta', dados)
  },

  async getAgendamentos(params?: { status?: string; page?: number }): Promise<{ data: Agendamento[], current_page: number, per_page: number, total: number, last_page: number }> {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.page) queryParams.append('page', String(params.page))

    const url = `/agendamentos${queryParams.toString() ? '?' + queryParams.toString() : ''}`
    const response = await apiClient.get<{ success: boolean; data: Agendamento[]; pagination: { current_page: number; per_page: number; total: number; last_page: number } }>(url)

    // Normalizar resposta da API
    if (response.success && response.pagination) {
      return {
        data: response.data || [],
        current_page: response.pagination.current_page,
        per_page: response.pagination.per_page,
        total: response.pagination.total,
        last_page: response.pagination.last_page
      }
    }

    // Fallback para formato direto
    return response as any
  },

  async getAgendamento(id: number): Promise<Agendamento> {
    try {
      // Tentar rota da API primeiro
      const response = await apiClient.get<{ success: boolean; data: Agendamento }>(`/agendamentos/${id}`)

      // Verificar se a resposta tem o formato esperado
      if (response && typeof response === 'object') {
        if ('success' in response && response.success && 'data' in response) {
          return response.data
        }
        // Se response já é o agendamento direto
        if ('id' in response && response.id === id) {
          return response as Agendamento
        }
      }

      throw new Error('Formato de resposta inválido')
    } catch (error: any) {
      // Se a API falhar, buscar da lista de agendamentos como fallback
      try {
        const agendamentosResponse = await this.getAgendamentos({ page: 1 })
        const agendamento = agendamentosResponse.data.find(a => a.id === id)

        if (agendamento) {
          return agendamento
        }

        throw new Error('Agendamento não encontrado')
      } catch (fallbackError: any) {
        // Se tudo falhar, lançar erro original
        throw new Error(error?.response?.data?.message || error?.message || 'Erro ao carregar agendamento')
      }
    }
  },

  async confirmarAgendamento(id: number): Promise<{ success: boolean; message: string }> {
    // Tentar API primeiro, depois web
    try {
      return await apiClient.post(`/agendamentos/${id}/confirmar`)
    } catch (error: any) {
      if (!shouldFallbackToLegacyWebRoute(error)) {
        throw error
      }
      // Fallback para rota web
      return await webClient.post(`/usuario/agendamentos/${id}/confirmar`)
    }
  },

  async cancelarAgendamento(id: number): Promise<{ success: boolean; message: string }> {
    const isNative = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.()

    // Priorizar endpoint POST /cancelar (mais compatível em mobile/proxy).
    try {
      return await apiClient.post(`/agendamentos/${id}/cancelar`)
    } catch (error: any) {
      // Fallback para versões de backend que só têm DELETE /agendamentos/{id}
      try {
        return await apiClient.delete(`/agendamentos/${id}`)
      } catch (deleteError: any) {
        // Em app nativo, não cair em rota web (exige CSRF de sessão e tende a retornar 419)
        if (isNative || !shouldFallbackToLegacyWebRoute(deleteError)) {
          throw deleteError
        }
        // Fallback para rota web apenas em ambiente browser/legado
        return await webClient.delete(`/usuario/agendamentos/${id}`)
      }
    }
  },

  async aceitarContraproposta(id: number, propostaId?: number): Promise<{ success: boolean; message: string }> {
    return await apiClient.post(`/agendamentos/${id}/aceitar-proposta`, { proposta_id: propostaId })
  },

  async recusarContraproposta(id: number): Promise<{ success: boolean; message: string }> {
    return await apiClient.post(`/agendamentos/${id}/recusar-proposta`)
  },

  async solicitarNegociacao(id: number, observacoes?: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post(`/agendamentos/${id}/solicitar-negociacao`, {
      observacoes
    })
  },

  async getSlotsDisponiveis(oficinaId: number, data: string, tipo: string = 'servico', duracao?: number): Promise<{ success: boolean; data: { slots: Slot[]; oficina: any; data: string; tipo: string } }> {
    const queryParams = new URLSearchParams()
    queryParams.append('data', data)
    queryParams.append('tipo', tipo)
    if (duracao) {
      queryParams.append('duracao', String(duracao))
    }

    return apiClient.get(`/usuario/oficinas/${oficinaId}/slots?${queryParams.toString()}`)
  },

  async aceitarPropostaEAagendar(id: number, dataHora: string, observacoes?: string, propostaId?: number): Promise<{ success: boolean; message: string; data?: any }> {
    return apiClient.post(`/agendamentos/${id}/aceitar-e-agendar`, {
      data_hora: dataHora,
      proposta_id: propostaId,
      observacoes
    }, {
      timeout: 30000 // 30 segundos
    })
  },

  async registerPushToken(data: {
    fcm_token: string
    device_id: string
    platform?: string
    device_name?: string
    app_type?: string
    email?: string
    device_os_version?: string
    app_version?: string
  }): Promise<{ success: boolean; message?: string }> {
    return apiClient.post('/usuario/push-token', data)
  },

  // ============================================
  // Pagamento de Agendamentos
  // ============================================

  async getAgendamentoPagamento(agendamentoId: number): Promise<{
    success: boolean
    data: {
      agendamento: {
        id: number
        codigo_agendamento: string
        titulo: string
        status: string
        data_hora_inicio: string | null
        oficina: { id: number; nome: string } | null
        veiculo: { id: number; marca: string; modelo: string; placa: string } | null
      }
      valor_total: number
      pagamento_aprovado: boolean
      pagamento: {
        id: number
        valor_total: number
        forma_pagamento: string
        data_pagamento: string | null
      } | null
      formas_pagamento: string[]
      pode_pagar: boolean
      bloqueio_pagamento?: {
        error_code?: string | null
        message?: string | null
      } | null
    }
  }> {
    return apiClient.get(`/agendamentos/${agendamentoId}/pagamento`)
  },

  async criarPagamentoPixAgendamento(agendamentoId: number, valor: number): Promise<{
    success: boolean
    message: string
    data?: {
      pagamento_id: number
      pix_code: string
      qr_code: string
      expires_at: string | null
      valor: number
    }
  }> {
    return apiClient.post(`/agendamentos/${agendamentoId}/pagamento/pix`, { valor })
  },

  async criarPagamentoCartaoAgendamento(agendamentoId: number, dados: {
    valor: number
    card_number: string
    card_holder_name: string
    card_expiration_month: string
    card_expiration_year: string
    card_cvv: string
    installments?: number
  }): Promise<{
    success: boolean
    message?: string
    status?: string
    pagamento_id?: number
    retry_allowed?: boolean
    reconciliation_required?: boolean
    data?: {
      pagamento_id: number
      transaction_id: string | null
      status: string
      valor: number
    }
  }> {
    return apiClient.post(`/agendamentos/${agendamentoId}/pagamento/cartao`, dados)
  },

  async getStatusPagamentoAgendamento(agendamentoId: number): Promise<{
    success: boolean
    data: {
      pagamento_aprovado: boolean
      pagamentos: Array<{
        id: number
        status: string
        valor_total: number
        forma_pagamento: string
        data_pagamento: string | null
        created_at: string
      }>
    }
  }> {
    return apiClient.get(`/agendamentos/${agendamentoId}/pagamento/status`)
  },

  // ============================================
  // Veículos do Usuário
  // ============================================

  async addVehicle(data: FormData): Promise<{ success: boolean; message: string; veiculo?: any }> {
    return apiClient.upload('/usuario/veiculos', data)
  },

  async updateVehicle(id: number, data: FormData): Promise<{ success: boolean; message: string; veiculo?: any }> {
    return apiClient.upload(`/usuario/veiculos/${id}`, data)
  },

  async deleteVehicle(id: number): Promise<{ success: boolean; message: string }> {
    return apiClient.delete(`/usuario/veiculos/${id}`)
  },

  async getVehicles(): Promise<{ success: boolean; data: any[] }> {
    return apiClient.get('/usuario/veiculos')
  },

  async getVehicle(id: number): Promise<{ success: boolean; veiculo?: any }> {
    return apiClient.get(`/usuario/veiculos/${id}`)
  },

  async buscarVeiculoPorPlaca(placa: string): Promise<VehicleLookupResponse> {
    const placaNormalizada = placa.toUpperCase().replace(/[^A-Z0-9]/g, '')
    return apiClient.post<VehicleLookupResponse>('/vehicles/placa', {
      placa: placaNormalizada
    })
  }
}

export { usuarioApi }
